import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {firstValueFrom} from 'rxjs';
import {CertificateAuthorityDto, CertificateAuthorityService} from './certificate-authority-service';
import {CertificateAuthorityModal} from './certificate-authority-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-certificate-authority',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './certificate-authority.html',
  styleUrl: './certificate-authority.scss'
})
export class CertificateAuthorityComponent implements OnInit {
  private readonly service = inject(CertificateAuthorityService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly searchQuery = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly showDateTime = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<CertificateAuthorityDto[]>([]);

  private totalElements = 0;

  constructor() {
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.getAllCertificateAuthorities(this.page() - 1, this.pageSize(), this.searchQuery(), this.stateFilter()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.stateFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(CertificateAuthorityModal, {size: 'lg'});
    modalRef.componentInstance.onSave = async (result: CertificateAuthorityDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createCertificateAuthority(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: CertificateAuthorityDto): void {
    const modalRef = this.modalService.open(CertificateAuthorityModal, {size: 'lg'});
    modalRef.componentInstance.certificateAuthority = item;
    modalRef.componentInstance.onSave = async (result: CertificateAuthorityDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateCertificateAuthority(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: CertificateAuthorityDto): void {
    const modalRef = this.modalService.open(CertificateAuthorityModal, {size: 'lg'});
    modalRef.componentInstance.certificateAuthority = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูล Certificate Authority นี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteCertificateAuthority(id).subscribe(() => this.load());
  }

  getDownloadFileName(ca: CertificateAuthorityDto): string {
    if (ca.certificateFileName) {
      return ca.certificateFileName;
    }
    if (!ca.certificateFile) {
      return 'certificate.crt';
    }
    const normalized = ca.certificateFile.replace(/\\/g, '/');
    const lastSlash = normalized.lastIndexOf('/');
    return lastSlash >= 0 ? normalized.substring(lastSlash + 1) : normalized;
  }

  canDownload(ca: CertificateAuthorityDto): boolean {
    return ca.id != null;
  }

  async downloadCertificateAuthority(ca: CertificateAuthorityDto, event?: Event): Promise<void> {
    event?.preventDefault();
    if (ca.id == null) {
      return;
    }
    try {
      const blob = await firstValueFrom(this.service.downloadCertificateAuthorityFile(ca.id));
      const fileName = this.getDownloadFileName(ca);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
    }
  }
}
